import dayjs from "dayjs"

import { RoutesPaths } from "../../../constants/routes.ts"
import type {
  NewEventItem,
  NewProjectItem,
  PendingApprovalItem
} from "../../../redux/notificationCounts/notification-counts.types.ts"
import { DATE_FORMAT } from "../../../utils/date/date.ts"
import { calendarDeepLink } from "../../Calendar/helpers/calendar-deep-link.ts"

// The nav badge says WHERE something happened; the destination says WHAT.
// One project → its own page; several → the grid, since there is no
// "recently created" filter on the Projects list to land more than one on
// (see the Phase 1 KB entry — no new backend for this pass).
export const projectDeepLink = (items: NewProjectItem[]): string =>
  items.length === 1
    ? RoutesPaths.project.view(items[0].id).overview
    : RoutesPaths.project.grid

// One event opens directly (the Calendar page already reads `?event=` and
// selects it — see calendar-deep-link.ts, which predates this feature).
// Several land the Calendar on the earliest new event's day, the closest
// thing to "here's what's new" the existing view supports.
export const eventDeepLink = (items: NewEventItem[]): string => {
  if (items.length === 1) {
    const date = items[0].startAt ? dayjs(items[0].startAt).format(DATE_FORMAT) : undefined
    return calendarDeepLink(date, items[0].id)
  }
  const dated = items.filter((item): item is NewEventItem & { startAt: string } =>
    Boolean(item.startAt)
  )
  if (!dated.length) return calendarDeepLink()
  const earliest = dated.reduce((min, item) =>
    dayjs(item.startAt).isBefore(dayjs(min.startAt)) ? item : min
  )
  return calendarDeepLink(dayjs(earliest.startAt).format(DATE_FORMAT))
}

// One proposal/contract → its own record; several (or none) → the list,
// which is also where a plain click on "Proposal Review"/"Contract Review"
// already went — no regression when nothing is pending.
export const approvalItemDeepLink = (
  kind: "proposal" | "contract",
  items: PendingApprovalItem[]
): string => {
  if (items.length === 1) {
    return kind === "proposal"
      ? RoutesPaths.proposal.view(items[0].id)
      : RoutesPaths.contract.view(items[0].id)
  }
  return kind === "proposal" ? RoutesPaths.proposal.list : RoutesPaths.contract.list
}
